import type { ActivityLog } from "@shared/schema";

// Turns activity_log rows into plain sentences for the Home "What changed"
// list, the Activity page and group/friend histories. Rows written before
// Sept 2026 have no meta/participants — they fall back to their stored
// description ("added Groceries · $45.00").

export type ActivityMeta = {
  description?: string;
  amount?: number;
  isSettlement?: boolean;
  paidById?: string;
  date?: string;
  count?: number;
  changes?: Record<string, { from: unknown; to: unknown }>;
};

export function parseMeta(row: ActivityLog): ActivityMeta {
  if (!row.meta) return {};
  try { return JSON.parse(row.meta) as ActivityMeta; } catch { return {}; }
}

export const money = (n: unknown) => (typeof n === "number" ? `$${n.toFixed(2)}` : "");

const shortDate = (v: unknown) => {
  if (typeof v !== "string") return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

/** One line describing what an edit changed, e.g. "$86.40 → $92.10 · spent date Sep 1 → Sep 3". */
export function editSummary(meta: ActivityMeta, nameOf: (id: string) => string): string {
  const c = meta.changes ?? {};
  const parts: string[] = [];
  if (c.amount) parts.push(`${money(c.amount.from)} → ${money(c.amount.to)}`);
  if (c.description) parts.push(`“${String(c.description.from)}” → “${String(c.description.to)}”`);
  if (c.date) parts.push(`spent date ${shortDate(c.date.from)} → ${shortDate(c.date.to)}`);
  const first = (id: unknown) => nameOf(String(id)).split(" ")[0];
  if (c.paidById) parts.push(`paid by ${first(c.paidById.from)} → ${first(c.paidById.to)}`);
  if (c.notes) parts.push("note changed");
  return parts.join(" · ");
}

export type ActivityLine = {
  actor: string;          // "You" / "Sarah"
  verb: string;           // "deleted"
  subject: string;        // "Pizza night"
  struck: boolean;        // strike the subject (deleted)
  where: string;          // "in Roommates" / "with Sarah" / ""
  detail: string;         // second line, e.g. edit summary
  amount: string;         // right-aligned amount, may be ""
  tag: "" | "Deleted" | "Edited" | "Restored";
};

export function describeActivity(
  row: ActivityLog,
  meId: string,
  nameOf: (id: string) => string,
  groupNameOf: (id: string) => string | undefined,
): ActivityLine {
  const meta = parseMeta(row);
  const actor = row.userId === meId ? "You" : row.userName.split(" ")[0];
  const nm = (id: string) => (id === meId ? "you" : nameOf(id).split(" ")[0]);
  const groupName = row.groupId ? groupNameOf(row.groupId) : undefined;
  const others = (row.participantIds ?? []).filter((id) => id !== meId && id !== row.userId);
  const where = groupName ? `in ${groupName}` : others.length === 1 ? `with ${nm(others[0])}` : "";
  const subject = meta.isSettlement ? "a settle-up" : meta.description ?? "";
  const base: ActivityLine = { actor, verb: "", subject, struck: false, where, detail: "", amount: "", tag: "" };

  switch (row.action) {
    case "expense_added":
      return { ...base, verb: "added", amount: money(meta.amount) };
    case "settled_up": {
      const to = (row.participantIds ?? []).find((id) => id !== meta.paidById);
      return {
        ...base,
        actor: meta.paidById ? (meta.paidById === meId ? "You" : nameOf(meta.paidById).split(" ")[0]) : actor,
        verb: "paid",
        subject: to ? nm(to) : "back",
        where: groupName ? `in ${groupName}` : "",
        amount: money(meta.amount),
      };
    }
    case "expense_edited":
      return { ...base, verb: "edited", detail: editSummary(meta, nameOf), tag: "Edited" };
    case "expense_deleted":
      return { ...base, verb: "deleted", struck: true, amount: money(meta.amount), tag: "Deleted" };
    case "expense_restored":
      return { ...base, verb: "restored", amount: money(meta.amount), tag: "Restored" };
    default:
      // expenses_cleared, expenses_imported, member_joined, member_added and
      // pre-Sept-2026 rows: the stored description already reads naturally.
      return { ...base, verb: row.description, subject: "", where: groupName && row.description.includes(groupName) ? "" : where };
  }
}

/** Is this row about the viewer? Used to keep Home's list personal. */
export function involvesMe(row: ActivityLog, meId: string): boolean {
  return row.userId === meId || (row.participantIds ?? []).includes(meId);
}

/** Relative time for a list: "2:14 pm" today, "Yesterday", else "Sep 22". */
export function whenLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startOfToday) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase();
  if (t >= startOfToday - 86400000) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function dayHeading(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  if (d.getTime() >= startOfToday) return "Today";
  if (d.getTime() >= startOfToday - 86400000) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
